<template>
  <!-- Bootstrap preloader for the first-ever visit only. From the second one
       on, the inline script in index.html has already applied the cached CI
       before the first paint and bootReady is true by the time this renders. -->
  <div
    v-if="!bootReady"
    class="cc-bootstrap-loader"
  >
    <v-progress-circular
      size="80"
      width="3"
      color="primary"
      indeterminate
    />
  </div>
  <!-- Hard-cut takeover during active maintenance on non-admin tenants.
       Replaces the regular layout — nothing else is reachable. -->
  <cc-maintenance-screen v-if="bootReady && maintenance" />
  <!-- Forced post-trial takeover: once the trial window has passed the customer
       is locked out of the app until we free them. Admins can record a decision
       on the screen; everyone else can only log out. -->
  <cc-trial-expiry-screen v-else-if="bootReady && store.trialLocked" />
  <template v-else>
    <!-- Banner sits above the layout: clean-layout (login / register /
         password-forgot) doesn't render its default slot, so anchoring the
         banner inside the layout would hide it on exactly the screens where
         users most need to see "Wartung am ..." ahead of time. -->
    <cc-maintenance-banner v-show="bootReady" />
    <component
      :is="layout"
      v-show="bootReady"
    >
      <router-view />
    </component>
  </template>
  <v-snackbar
    v-model="showToast"
    :timer="('3000' as any)"
    color="success"
    location="top"
  >
    <template #text>
      <v-row class="d-flex align-center">
        <v-col
          cols="1"
          class="mr-2"
        >
          <v-icon
            size="x-large"
            :icon="Icons.CHECK_CIRCLE"
          />
        </v-col>
        <v-col cols="10">
          <div v-dompurify-html="toastText" />
        </v-col>
      </v-row>
    </template>
  </v-snackbar>
  <v-snackbar
    v-model="showTrialError"
    :timer="('5000' as any)"
    color="error"
    location="top"
  >
    <template #text>
      <v-row class="d-flex align-center">
        <v-col
          cols="1"
          class="mr-2"
        >
          <v-icon
            size="x-large"
            :icon="Icons.ALERT_CIRCLE"
          />
        </v-col>
        <v-col cols="10">
          <div>{{ trialErrorText }}</div>
        </v-col>
      </v-row>
    </template>
  </v-snackbar>
  <v-overlay
    v-model="layoutLoading"
    class="cc-application-loader align-center justify-center"
    scroll-strategy="block"
    persistent
  >
    <v-progress-circular
      size="120"
      width="3"
      color="primary"
      indeterminate
    />
    <div class="mt-5">
      {{ t('common.application.loading') }}
    </div>
  </v-overlay>
</template>

<script setup lang="ts">
//================= [CSS] =====================================

//================= [JS] ======================================
import Cookies from 'js-cookie';
import {computed, nextTick, onBeforeMount, ref, watch} from 'vue';
import {RouteLocationNormalizedLoaded, useRouter} from 'vue-router';
import {useI18n} from 'vue-i18n';
import {useLocale} from 'vuetify';
import {ApiClient, Icons, useEventBus} from '@simpleforyou/client-core';
import {
  checkCoaching,
  checkRole,
  checkUserType,
  initialize,
  initialized,
  layoutLoading,
} from '@/composables/layout/layoutHandler';
import {
  loadFooterLinks,
  loadLayout,
  loadNotifications,
  loadPartnerShops,
  loadSocialMedia,
} from '@/composables/layout/baseLayout';
import {waitForCustomerStyle} from '@/composables/layout/useCustomerStyle';
import {loadMaintenance, maintenance} from '@/composables/common/maintenance/useMaintenance';
import CcMaintenanceBanner from '@/components/common/maintenance/CcMaintenanceBanner.vue';
import CcMaintenanceScreen from '@/components/common/maintenance/CcMaintenanceScreen.vue';
import CcTrialExpiryScreen from '@/components/common/trial/CcTrialExpiryScreen.vue';
import {Events} from '@/common/utils/Events';
import {useAuth} from '@/composables/useAuth';
import {useNotificationStream} from '@/composables/useNotificationStream';
import {Routes} from '@/common/enums/Routes';
import {useStore} from '@/common/store/store';
import {SecUtil} from '@/common/utils/security/SecUtil';
import {Permissions} from '@simpleforyou/coaching-shared';
//================= [VUE COMPONENTS] ==========================
const store = useStore();
const router = useRouter();
const {availableLocales, locale, t} = useI18n();
const {current} = useLocale();
const {refresh, isLoggedIn} = useAuth();

const eventBus = useEventBus();
const {connect: connectNotificationStream, disconnect: disconnectNotificationStream} = useNotificationStream(refresh);

const showToast = ref(false);
const toastText = ref('');
const showTrialError = ref(false);
const trialErrorText = ref('');
const bootReady = ref(false);

const TRIAL_LIMIT_KEYS: Record<string, string> = {
  TRIAL_LIMIT_USERS: 'trial.limit.users',
  TRIAL_LIMIT_MODULES: 'trial.limit.modules',
  TRIAL_LIMIT_COURSES: 'trial.limit.courses',
  TRIAL_LIMIT_STORAGE: 'trial.limit.storage',
  TRIAL_LIMIT_RECIPE_RATING: 'trial.limit.rating',
};

// Surface a trial-limit rejection (pushed onto the store by the API
// interceptor) as an error toast, then clear it so the next hit re-triggers.
watch(() => store.trialLimitCode, (code) => {
  if (!code) {
    return;
  }
  showTrialError.value = false;
  trialErrorText.value = '';
  nextTick(() => {
    trialErrorText.value = t(TRIAL_LIMIT_KEYS[code] ?? 'trial.limit.generic');
    showTrialError.value = true;
    store.setTrialLimitCode(null);
  });
});

const layout = computed(() => {
  const meta = router.currentRoute.value.meta;
  if (meta.hasOwnProperty('layout')) {
    if (meta.layout === 'flex') {
      return `${store.isAdmin ? 'main' : 'user'}-layout`;
    } else {
      return `${meta.layout}-layout`;
    }
  }
  return 'main-layout';
});

onBeforeMount(async () => {
  // Only the style gates the first render, and on a repeat visit it doesn't
  // even do that: the inline script in index.html applies the cached CI before
  // the first paint, so this resolves synchronously and the preloader is never
  // seen. It still has to be awaited for the first-ever visit, where there is
  // nothing cached yet and revealing the app early would show default branding.
  await waitForCustomerStyle();
  bootReady.value = true;

  // Deliberately NOT awaited. Blocking the boot on it made every user wait for
  // a request whose answer is "no maintenance" on all but a handful of days a
  // year. The takeover screen is driven by a reactive flag, so it appears the
  // moment the answer arrives — a fraction of a second into a running app
  // instead of in front of a spinner.
  void loadMaintenance();

  if (!isLoggedIn()) {
    await refresh();
  }

  watch([router.currentRoute, () => isLoggedIn()], async ([to]) => {
    const toRoute = to as RouteLocationNormalizedLoaded;
    if (isLoggedIn()) {
      if (!initialized.value) {
        layoutLoading.value = true;
        await initialize();
        await loadLayout();
        layoutLoading.value = false;
        if (eventBus === undefined) {
          return;
        }
        eventBus.on(Events.NOTIFY, (text: string) => {
          showToast.value = false;
          toastText.value = '';
          nextTick(() => {
            toastText.value = text;
            showToast.value = true;
          });
        });
        eventBus.on(Events.RELOAD_FOOTER_LINKS, () => {
          loadFooterLinks();
        });
        eventBus.on(Events.RELOAD_SOCIAL_MEDIA, () => {
          loadSocialMedia();
        });
        eventBus.on(Events.RELOAD_PARTNER_SHOPS, () => {
          loadPartnerShops();
        });
      }
      // Onboarding gate: the coach (the customer's main user) without a coaching
      // profile must create one first. The onboarding route itself is exempt.
      if (
        store.userData?.main_user
        && store.userData?.coaching_profile_complete === false
        && toRoute.name !== Routes.COACHING_PROFILE_ONBOARDING
      ) {
        await router.push({name: Routes.COACHING_PROFILE_ONBOARDING});
        return;
      }
      if (toRoute.name === Routes.LOGIN) {
        if (!store.isAdmin) {
          await router.push({name: Routes.USER_DASHBOARD});
        } else {
          if (SecUtil.hasRoles(Permissions.ROLE_MODULE_INDEX)) {
            await router.push({name: Routes.MODULE_LIST});
          } else {
            await router.push({name: Routes.USER_PROFILE});
          }
        }
      } else {
        // const layoutMeta = toRoute.meta.layout;
        // if (layoutMeta === 'user' && !store.isAdmin) {
        //   await router.push({name: Routes.USER_DASHBOARD});
        // } else if (!layoutMeta && store.isAdmin) {
        //   if (SecUtil.hasRoles(Permissions.ROLE_MODULE_INDEX)) {
        //     await router.push({name: Routes.MODULE_LIST});
        //   } else {
        //     await router.push({name: Routes.USER_PROFILE});
        //   }
        // }
      }
      await checkRole(to);
      await checkCoaching(to);
      await checkUserType(to);
      await loadNotifications();
      // Live notification stream (SSE) — idempotent, stays open across navigation.
      connectNotificationStream();
    } else if (toRoute.matched.length > 0 && !toRoute.meta.noAuthRequired) {
      disconnectNotificationStream();
      await router.push({name: Routes.LOGIN});
    }
  }, {immediate: true});

  const i18nCookie = Cookies.get('i18n');
  if (!i18nCookie) {
    const expireDate = new Date();
    expireDate.setHours(expireDate.getHours() + 4);
    Cookies.set('i18n', locale.value, {expires: expireDate, SameSite: 'Strict', secure: true});
  } else {
    if (availableLocales.includes(i18nCookie)) {
      locale.value = i18nCookie;
    } else {
      locale.value = 'de';
      const expireDate = new Date();
      expireDate.setHours(expireDate.getHours() + 4);
      Cookies.set('i18n', locale.value, {expires: expireDate, SameSite: 'Strict', secure: true});
    }
  }
  current.value = locale.value;
});

ApiClient.registerCacheHandler();

// BUGFIX For ResizeObserver https://github.com/vuejs/vue-cli/issues/7431
const debounce = (callback: (...args: any[]) => void, delay: number) => {
  let tid: any;
  return function (...args: any[]) {
    const ctx = self;
    tid && clearTimeout(tid);
    tid = setTimeout(() => {
      callback.apply(ctx, args);
    }, delay);
  };
};

const _ = (window as any).ResizeObserver;
(window as any).ResizeObserver = class ResizeObserver extends _ {
  constructor(callback: (...args: any[]) => void) {
    callback = debounce(callback, 20);
    super(callback);
  }
};

</script>

<style lang="scss">
@forward "@/assets/scss/application.scss";

.cc-bootstrap-loader {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background, #f5f5f5);
  z-index: 99999;
}
</style>
